// src/components/Plumber/TaskerProfileEdit.js
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Camera,
  User,
  Phone,
  Mail,
  MapPin,
  Clock,
  DollarSign,
  Globe,
  ChevronDown,
  ChevronUp,
  LocateFixed,
} from "lucide-react";

import { Button } from "../ui/Button";
import { useAuth } from "../../contexts/AuthContext";
import { auth, db, storage } from "../../firebase";

import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";

import MapPicker from "../maps/MapPicker";
import useUserGeolocation from "../../hooks/useUserGeolocation";
import { reverseGeocode } from "../../lib/geocoding";

// -----------------------------
// Constants
// -----------------------------
const DEFAULT_RIYADH = { lat: 24.7136, lng: 46.6753 };

const SAUDI_CITIES = [
  "Abha",
  "Al-Ahsa",
  "Al Bahah",
  "Al-Kharj",
  "Al-Qassim",
  "Arar",
  "Buraydah",
  "Dammam",
  "Dhahran",
  "Hail",
  "Hafar Al-Batin",
  "Jazan",
  "Jeddah",
  "Jubail",
  "Khamis Mushait",
  "Khobar",
  "Mecca",
  "Medina",
  "Najran",
  "Qatif",
  "Riyadh",
  "Sakakah",
  "Tabuk",
  "Taif",
  "Yanbu",
];

// -----------------------------
// Helpers
// -----------------------------
const safeString = (v, fallback = "") => (typeof v === "string" ? v : fallback);

const stripSaudiPrefix = (val) => {
  const s = (val ?? "").toString().trim();
  return s
    .replace(/^(\+?966|00966)\s?/, "")
    .replace(/\s/g, "")
    .replace(/[^\d]/g, "");
};

const formatSaudiPhone = (digits) => {
  const d = (digits ?? "").toString().replace(/[^\d]/g, "");
  if (!d) return "";
  return `+966 ${d}`.trim();
};

const isValidLatLng = (loc) => {
  const lat = Number(loc?.lat);
  const lng = Number(loc?.lng);
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
  );
};

const toNumberOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const normalizePriceRange = (input) => {
  const fallback = { min: null, max: null, currency: "SAR" };
  if (!input) return fallback;

  if (typeof input === "object") {
    return {
      min: toNumberOrNull(input.min),
      max: toNumberOrNull(input.max),
      currency: typeof input.currency === "string" ? input.currency : "SAR",
    };
  }

  if (typeof input === "string") {
    const nums = input.match(/\d+/g)?.map(Number) || [];
    return {
      min: Number.isFinite(nums[0]) ? nums[0] : null,
      max: Number.isFinite(nums[1]) ? nums[1] : null,
      currency: "SAR",
    };
  }

  return fallback;
};

// -----------------------------
// Component
// -----------------------------
export default function TaskerProfileEdit() {
  const navigate = useNavigate();
  const { profile, loading: authLoading } = useAuth();
  const uid = profile?.uid || auth?.currentUser?.uid || null;

  // UX: collapsible location section => geolocation only when opened / requested
  const [locationOpen, setLocationOpen] = useState(false);

  // UX: button “Use My Location” triggers location flow
  const [geoEnabled, setGeoEnabled] = useState(false);
  const [pendingUseMyLocation, setPendingUseMyLocation] = useState(false);
  const geo = useUserGeolocation({ enabled: geoEnabled });

  const [tasker, setTasker] = useState({
    name: "",
    email: "",
    phone: "",
    country: "Saudi Arabia",
    city: "",
    bio: "",
    imageUrl: "",

    availability: "Today",
    responseTime: "< 30 min",

    priceRange: { min: null, max: null, currency: "SAR" },

    addressText: "",
    location: DEFAULT_RIYADH,
    serviceRadiusKm: 15,
  });

  const [phoneNumber, setPhoneNumber] = useState("");

  const [geoErrorMsg, setGeoErrorMsg] = useState("");
  const geocodeReqIdRef = useRef(0);

  const [saving, setSaving] = useState(false);

  // photo upload
  const fileInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // -----------------------------
  // Load from Firestore (taskers/{uid}) + fallback seed from useAuth profile
  // -----------------------------
  useEffect(() => {
    if (!uid) return;

    const ref = doc(db, "taskers", uid);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const rp = profile?.roleProfile || {};
        const data = snap.exists() ? snap.data() : {};

        const merged = {
          // identity
          name: safeString(data?.name, safeString(rp?.name, safeString(profile?.name, ""))),
          email: safeString(
            data?.email,
            safeString(rp?.email, safeString(profile?.email, safeString(auth?.currentUser?.email, "")))
          ),
          phone: safeString(data?.phone, safeString(rp?.phone, "")),
          country: "Saudi Arabia",
          city: safeString(data?.city, safeString(rp?.city, "")),
          bio: safeString(data?.bio, safeString(rp?.bio, "")),
          imageUrl: safeString(data?.imageUrl, safeString(rp?.imageUrl, safeString(rp?.photoURL, ""))),

          availability: safeString(data?.availability, safeString(rp?.availability, "Today")),
          responseTime: safeString(data?.responseTime, safeString(rp?.responseTime, "< 30 min")),

          priceRange: normalizePriceRange(data?.priceRange ?? rp?.priceRange),

          addressText: safeString(data?.addressText, ""),
          location: isValidLatLng(data?.location)
            ? { lat: Number(data.location.lat), lng: Number(data.location.lng) }
            : DEFAULT_RIYADH,
          serviceRadiusKm: Number.isFinite(Number(data?.serviceRadiusKm))
            ? Math.max(1, Math.min(100, Number(data.serviceRadiusKm)))
            : 15,
        };

        setTasker((prev) => ({ ...prev, ...merged }));

        const digits = stripSaudiPrefix(merged.phone);
        setPhoneNumber(digits);
      },
      (err) => {
        console.error("taskers/{uid} snapshot error:", err);
      }
    );

    return () => unsub();
  }, [uid, profile]);

  // -----------------------------
  // When location section expands, enable geolocation (trigger hook)
  // -----------------------------
  useEffect(() => {
    if (!locationOpen) return;
    setGeoEnabled(true);
  }, [locationOpen]);

  // -----------------------------
  // "Use My Location" flow
  // -----------------------------
  const handleUseMyLocation = () => {
    setLocationOpen(true);
    setGeoEnabled(true);

    if (isValidLatLng(geo?.coords)) {
      setTasker((prev) => ({
        ...prev,
        location: { lat: geo.coords.lat, lng: geo.coords.lng },
      }));
      return;
    }

    setPendingUseMyLocation(true);
  };

  useEffect(() => {
    if (!pendingUseMyLocation) return;
    if (!isValidLatLng(geo?.coords)) return;

    setTasker((prev) => ({
      ...prev,
      location: { lat: geo.coords.lat, lng: geo.coords.lng },
    }));
    setPendingUseMyLocation(false);
  }, [pendingUseMyLocation, geo?.coords?.lat, geo?.coords?.lng]);

  // -----------------------------
  // Phone input handling (Saudi)
  // -----------------------------
  const handlePhoneNumberChange = (e) => {
    const newDigits = (e.target.value ?? "").toString();
    if (!/^\d*$/.test(newDigits)) return;

    setPhoneNumber(newDigits);
    setTasker((prev) => ({ ...prev, phone: formatSaudiPhone(newDigits) }));
  };

  // -----------------------------
  // Reverse geocode when location changes (ONLY when section open)
  // -----------------------------
  const reverseGeocodeAndSetAddress = async (coords) => {
    if (!isValidLatLng(coords)) return;

    const reqId = ++geocodeReqIdRef.current;
    setGeoErrorMsg("");

    try {
      const { formattedAddress } = await reverseGeocode(coords, {
        cacheTtlMs: 5 * 60 * 1000,
        cachePrecision: 6,
      });

      if (reqId !== geocodeReqIdRef.current) return;

      const nice = (formattedAddress || "").toString().trim();
      setTasker((prev) => ({
        ...prev,
        addressText: nice,
      }));
    } catch (e) {
      if (reqId !== geocodeReqIdRef.current) return;

      const msg = (e?.message || "").toString();
      if (msg.includes("ZERO_RESULTS")) {
        setTasker((prev) => ({ ...prev, addressText: "" }));
        setGeoErrorMsg("");
        return;
      }

      console.warn("Reverse geocode failed:", e);
      setGeoErrorMsg(
        "Geocoder failed. Check: Billing enabled + Geocoding API enabled + API key restrictions."
      );
    }
  };

  useEffect(() => {
    if (!locationOpen) return;
    if (!isValidLatLng(tasker.location)) return;

    const t = setTimeout(() => {
      reverseGeocodeAndSetAddress({
        lat: Number(tasker.location.lat),
        lng: Number(tasker.location.lng),
      });
    }, 250);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationOpen, tasker.location?.lat, tasker.location?.lng]);

  // -----------------------------
  // Photo upload (Storage + Firestore)
  // -----------------------------
  const openFilePicker = () => {
    if (!uid || uploadingPhoto) return;
    fileInputRef.current?.click();
  };

  const uploadTaskerAvatar = async (file) => {
    if (!uid || !file) return;

    const isImage = file.type?.startsWith("image/");
    const maxBytes = 5 * 1024 * 1024;

    if (!isImage) return alert("Please select an image file (JPG/PNG).");
    if (file.size > maxBytes) return alert("Max file size is 5MB.");

    setUploadingPhoto(true);
    setUploadProgress(0);

    try {
      const ext = (file.name?.split(".").pop() || "jpg").toLowerCase();
      const path = `profiles/taskers/${uid}/avatar.${ext}`;
      const sRef = storageRef(storage, path);

      const task = uploadBytesResumable(sRef, file, {
        contentType: file.type || "image/jpeg",
      });

      const downloadURL = await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => {
            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
            setUploadProgress(Number.isFinite(pct) ? pct : 0);
          },
          (err) => reject(err),
          async () => resolve(await getDownloadURL(task.snapshot.ref))
        );
      });

      // upsert (in case tasker doc does not exist yet)
      await setDoc(
        doc(db, "taskers", uid),
        {
          imageUrl: downloadURL,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setTasker((prev) => ({ ...prev, imageUrl: downloadURL }));
    } catch (err) {
      console.error("Tasker avatar upload error:", err);
      alert("Upload failed. Check Storage rules & authentication.");
    } finally {
      setUploadingPhoto(false);
      setUploadProgress(0);
    }
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0] || null;
    e.target.value = "";
    if (!file) return;
    await uploadTaskerAvatar(file);
  };

  const displayImage = useMemo(() => {
    return (
      tasker.imageUrl ||
      "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=150&h=150&fit=crop"
    );
  }, [tasker.imageUrl]);

  // -----------------------------
  // Save to Firestore (taskers/{uid})
  // -----------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!uid) return;

    const min = toNumberOrNull(tasker.priceRange?.min);
    const max = toNumberOrNull(tasker.priceRange?.max);

    const fixedPriceRange =
      min !== null && max !== null && min > max
        ? { min: max, max: min, currency: "SAR" }
        : { min, max, currency: "SAR" };

    const radius = Number(tasker.serviceRadiusKm);
    const safeRadius = Number.isFinite(radius) ? Math.max(1, Math.min(100, radius)) : 15;

    const safeLoc = isValidLatLng(tasker.location)
      ? { lat: Number(tasker.location.lat), lng: Number(tasker.location.lng) }
      : DEFAULT_RIYADH;

    try {
      setSaving(true);

      // upsert (handles first time profile)
      await setDoc(
        doc(db, "taskers", uid),
        {
          name: safeString(tasker.name).trim(),
          email: safeString(tasker.email).trim(),
          phone: safeString(tasker.phone).trim(),
          country: "Saudi Arabia",
          city: safeString(tasker.city).trim(),

          bio: safeString(tasker.bio).trim(),
          imageUrl: safeString(tasker.imageUrl).trim(),

          availability: safeString(tasker.availability, "Today").trim(),
          responseTime: safeString(tasker.responseTime, "< 30 min").trim(),

          priceRange: fixedPriceRange,

          addressText: safeString(tasker.addressText).trim(),
          location: safeLoc,
          serviceRadiusKm: safeRadius,

          updatedAt: serverTimestamp(),
          // keep createdAt if already exists; only set if missing
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );

      navigate("/tasker/dashboard");
    } catch (err) {
      console.error("Save tasker profile error:", err);
      alert("Save failed. Check Firestore rules & authentication.");
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------
  // UI
  // -----------------------------
  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-600"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-xl font-bold text-[#1E3A8A]">Edit Profile</h1>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Photo */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-[#1E3A8A] mb-6">Profile Photo</h2>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onFileSelected}
              className="hidden"
            />

            <div className="flex items-center gap-6">
              <div className="relative">
                <img
                  src={displayImage}
                  alt={tasker.name || "Tasker"}
                  className="w-24 h-24 rounded-full object-cover border-4 border-gray-100"
                />
                <button
                  type="button"
                  className="absolute bottom-0 right-0 bg-[#006C35] text-white p-2 rounded-full hover:bg-[#005428] shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={openFilePicker}
                  disabled={!uid || uploadingPhoto}
                >
                  <Camera size={16} />
                </button>
              </div>

              <div className="text-sm text-gray-500">
                <p>Allowed formats: JPG, PNG</p>
                <p>Max size: 5MB</p>

                {uploadingPhoto ? (
                  <div className="mt-3">
                    <div className="text-xs text-gray-600 mb-1">Uploading... {uploadProgress}%</div>
                    <div className="w-56 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-[#006C35]" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-[#1E3A8A] mb-6">Basic Information</h2>

            <div className="space-y-4">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={tasker.name}
                    onChange={(e) => setTasker((p) => ({ ...p, name: e.target.value }))}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006C35] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="email"
                      value={tasker.email || ""}
                      onChange={(e) => setTasker((p) => ({ ...p, email: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006C35] focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <div className="flex gap-2">
                    <div className="flex items-center px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 text-sm w-28 select-none">
                      <span className="mr-2">🇸🇦</span> +966
                    </div>
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006C35] focus:border-transparent"
                        placeholder="55 123 4567"
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      value="Saudi Arabia"
                      disabled
                      readOnly
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 bg-gray-50 rounded-lg text-gray-500 cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <select
                      value={tasker.city || ""}
                      onChange={(e) => setTasker((p) => ({ ...p, city: e.target.value }))}
                      className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006C35] focus:border-transparent appearance-none bg-white"
                    >
                      <option value="" disabled>
                        Select City
                      </option>
                      {SAUDI_CITIES.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      size={16}
                    />
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <textarea
                  value={tasker.bio}
                  onChange={(e) => setTasker((p) => ({ ...p, bio: e.target.value }))}
                  rows={4}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006C35] focus:border-transparent"
                  placeholder="Tell customers about your experience..."
                />
              </div>
            </div>
          </div>

          {/* Location (Collapsible) */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#1E3A8A] flex items-center gap-2">
                <MapPin size={20} />
                Location & Service Radius
              </h2>

              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={handleUseMyLocation} className="h-9">
                  <LocateFixed size={16} className="mr-2" />
                  Use My Location
                </Button>

                <button
                  type="button"
                  onClick={() => setLocationOpen((v) => !v)}
                  className="text-sm font-medium text-[#1E3A8A] flex items-center gap-1 hover:opacity-80"
                >
                  {locationOpen ? "Hide" : "Show"}
                  {locationOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
              </div>
            </div>

            {!locationOpen ? (
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="text-sm text-gray-700">
                  {tasker.addressText?.trim() ? tasker.addressText : "Click Show to set your location."}
                </div>
                {isValidLatLng(tasker.location) ? (
                  <div className="text-xs text-gray-500 mt-1">
                    Lat: {Number(tasker.location.lat).toFixed(6)} | Lng: {Number(tasker.location.lng).toFixed(6)} • Radius:{" "}
                    {Number(tasker.serviceRadiusKm || 0) || 15} km
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                <MapPicker
                  active={locationOpen}
                  height={240}
                  userCenter={isValidLatLng(geo?.coords) ? geo.coords : DEFAULT_RIYADH}
                  userZoom={17}
                  allowFullscreen={true}
                  enableMyLocationButton={true}
                  value={isValidLatLng(tasker.location) ? tasker.location : DEFAULT_RIYADH}
                  defaultCenter={isValidLatLng(tasker.location) ? tasker.location : DEFAULT_RIYADH}
                  defaultZoom={15}
                  onChange={(coords) => {
                    if (!isValidLatLng(coords)) return;
                    setTasker((prev) => ({ ...prev, location: { lat: coords.lat, lng: coords.lng } }));
                  }}
                  showRadiusCircle={true}
                  radiusKm={tasker.serviceRadiusKm ?? null}
                />

                <div className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>

                    <div className="flex items-start gap-2 bg-white border border-gray-300 rounded-lg p-3">
                      <MapPin className="text-[#006C35] mt-0.5" size={18} />
                      <div className="flex-1">
                        <div className="text-sm text-gray-800">
                          {tasker.addressText?.trim()
                            ? tasker.addressText
                            : isValidLatLng(tasker.location)
                            ? "No address found here — move the pin slightly."
                            : "Move the pin to select your location."}
                        </div>

                        {geoErrorMsg ? (
                          <div className="text-xs text-red-600 mt-1">{geoErrorMsg}</div>
                        ) : isValidLatLng(tasker.location) ? (
                          <div className="text-xs text-gray-500 mt-1">
                            Lat: {Number(tasker.location.lat).toFixed(6)} | Lng: {Number(tasker.location.lng).toFixed(6)}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500 mt-1">
                            Tip: Click “Use My Location” to start from your current position.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service Radius (km)</label>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={tasker.serviceRadiusKm ?? ""}
                        onChange={(e) =>
                          setTasker((prev) => ({
                            ...prev,
                            serviceRadiusKm: parseInt(e.target.value, 10) || 0,
                          }))
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006C35] focus:border-transparent"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">km</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">How far you are willing to travel for jobs.</p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Professional Details */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-[#1E3A8A] mb-6">Professional Details</h2>

            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Availability Status</label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <select
                      value={tasker.availability}
                      onChange={(e) => setTasker((p) => ({ ...p, availability: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006C35] focus:border-transparent"
                    >
                      <option value="Today">Today</option>
                      <option value="Tomorrow">Tomorrow</option>
                      <option value="This Week">This Week</option>
                      <option value="Weekend Only">Weekend Only</option>
                      <option value="Unavailable">Unavailable</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Response Time</label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <select
                      value={tasker.responseTime}
                      onChange={(e) => setTasker((p) => ({ ...p, responseTime: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006C35] focus:border-transparent"
                    >
                      <option value="< 15 min">&lt; 15 min</option>
                      <option value="< 30 min">&lt; 30 min</option>
                      <option value="< 1 hour">&lt; 1 hour</option>
                      <option value="Same Day">Same Day</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">General Price Range</label>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="number"
                      inputMode="numeric"
                      value={tasker.priceRange.min ?? ""}
                      onChange={(e) =>
                        setTasker((p) => ({
                          ...p,
                          priceRange: { ...p.priceRange, min: toNumberOrNull(e.target.value) },
                        }))
                      }
                      placeholder="Min"
                      className="w-full pl-10 pr-16 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006C35] focus:border-transparent"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">SAR</span>
                  </div>

                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="number"
                      inputMode="numeric"
                      value={tasker.priceRange.max ?? ""}
                      onChange={(e) =>
                        setTasker((p) => ({
                          ...p,
                          priceRange: { ...p.priceRange, max: toNumberOrNull(e.target.value) },
                        }))
                      }
                      placeholder="Max"
                      className="w-full pl-10 pr-16 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006C35] focus:border-transparent"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">SAR</span>
                  </div>
                </div>

                <p className="text-xs text-gray-400 mt-2">
                  Preview:{" "}
                  {tasker.priceRange.min == null && tasker.priceRange.max == null
                    ? "-"
                    : `${tasker.priceRange.min ?? ""}${
                        tasker.priceRange.min != null && tasker.priceRange.max != null ? " – " : ""
                      }${tasker.priceRange.max ?? ""} SAR`}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate(-1)} className="px-6">
              Cancel
            </Button>

            <Button
              type="submit"
              className="bg-[#006C35] hover:bg-[#005428] px-8"
              disabled={saving || authLoading || !uid || uploadingPhoto}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
